// src/app/pages/VehicleMovementReport/worksheetGenerator.ts
import ExcelJS, { Workbook, Worksheet, Cell } from 'exceljs'

/**
 * generateLoginstatusWorksheet
 * - workbook: ExcelJS Workbook
 * - data: rows (trip_bus, driver_name, conductor_name, trip_out, checked_in, sign, status)
 * - titleMap: label mapping
 * - imgUrl: full URL to logo image (browser-accessible, CORS enabled)
 */
export async function generateLoginstatusWorksheet(
  workbook: Workbook,
  data: Array<Record<string, any>>,
  titleMap: Record<string, string>,
  imgUrl?: string
): Promise<Worksheet> {
  const ws: Worksheet = workbook.addWorksheet('Vehicle Movement Report')
const colWidths = [50, 50, 50, 50, 50, 50, 50, 50];
for (let i = 1; i <= colWidths.length; i++) {
  ws.getColumn(i).width = colWidths[i - 1];
}
  // Layout rows/columns (modifiable)
  const LOGO_MERGE_TOP_ROW = 1
  const LOGO_MERGE_BOTTOM_ROW = 3 // A1:H3 (logo container)
  const HEADER_MERGE_TOP_ROW = 4
  const HEADER_MERGE_BOTTOM_ROW = 6 // A4:H6 (header text)
  const TABLE_HEADER_ROW1 = HEADER_MERGE_BOTTOM_ROW + 1 // e.g., 7
  const TABLE_HEADER_ROW2 = HEADER_MERGE_BOTTOM_ROW + 2 // e.g., 8
  const DATA_START_ROW = HEADER_MERGE_BOTTOM_ROW + 3 // e.g., 9

  // -------------------------
  // 1) Merge logo container A1:H3 (no border)
  // -------------------------
  ws.mergeCells(LOGO_MERGE_TOP_ROW, 1, LOGO_MERGE_BOTTOM_ROW, 8) // A1:H3
  // do not set border on merged logo container so no line shows beneath it
  // adjust row heights for the logo container rows
  ws.getRow(1).height = 30 // increase top row for more vertical room for logo
  ws.getRow(2).height = 30
  ws.getRow(3).height = 30

  // -------------------------
  // 2) Add logo centered inside that container (narrower width, increased height)
  //    We'll make it narrower (approx columns D..E) and taller (rows 0..3)
  // -------------------------
  if (imgUrl) {
  try {
    const response = await fetch(imgUrl, { mode: 'cors' })
    if (response.ok) {
      const arrayBuffer = await response.arrayBuffer()
      const extension: 'jpeg' | 'png' = imgUrl.toLowerCase().endsWith('.png') ? 'png' : 'jpeg'
      const imageId = workbook.addImage({ buffer: arrayBuffer as any, extension })

      // Calculate center of logo container (A1:H3)
      const colStart = 0    // A=0
      const colEnd = 8      // H=8
      const rowStart = 0    // Row 1
      const rowEnd = 3      // Row 3

      // Desired logo size (in cells)
      const logoWidthCells = 1.5   // width in columns
      const logoHeightCells = 3  // height in rows

      // Calculate center of merged container
      const containerWidth = colEnd - colStart
      const containerHeight = rowEnd - rowStart

      const tlCol = colStart + (containerWidth - logoWidthCells) / 2
      const tlRow = rowStart + (containerHeight - logoHeightCells) / 2
      const brCol = tlCol + logoWidthCells
      const brRow = tlRow + logoHeightCells

      ws.addImage(imageId, {
        tl: { col: tlCol, row: tlRow } as any,
        br: { col: brCol, row: brRow } as any,
        editAs: 'oneCell',
      })
    }
  } catch (err) {
    console.error('Logo fetch failed:', err)
  }
}


  // -------------------------
  // 3) Header text block A4:H6 (below logo container)
  //    Increase the row heights so multiline header text is visible without clicking
  // -------------------------
  ws.mergeCells(HEADER_MERGE_TOP_ROW, 1, HEADER_MERGE_BOTTOM_ROW, 8) // A4:H6
  const headerCell = ws.getCell(HEADER_MERGE_TOP_ROW, 1)
  headerCell.value = [
    'M.E.S. INDIAN SCHOOL, DOHA-QATAR',
    'TRANSPORT SECTION',
    'VEHICLE MOVEMENT & DISEMBARKATION RECORD',
    '(To ensure Time Keeping & Safety – Regular Trips)',
  ].join('\n')
  headerCell.alignment = { vertical: 'top', horizontal: 'center', wrapText: true }
  headerCell.font = { bold: true, size: 12 }

  // Increase header rows heights to display all lines comfortably
  ws.getRow(HEADER_MERGE_TOP_ROW).height = 36
  ws.getRow(HEADER_MERGE_TOP_ROW + 1).height = 36
  ws.getRow(HEADER_MERGE_TOP_ROW + 2).height = 36

  // -------------------------
  // 4) Two-row table header (immediately below header block)
  //    Remove top border from first header row so no visible line under header block
  // -------------------------
  // Row 1 (group labels)
  ws.getRow(TABLE_HEADER_ROW1).values = [
    titleMap?.sl_no ?? 'SL NO', // A
    titleMap?.trip_bus ?? 'Bus No', // B
    'Name of', // C (will span C:D)
    null, // D placeholder
    'Time', // E (will span E:F)
    null, // F
    titleMap?.sign ?? 'Driver', // G
    titleMap?.status ?? 'Remark', // H
  ]

  // Row 2 (sub-headers)
  ws.getRow(TABLE_HEADER_ROW2).values = [
    '', // A
    '', // B
    titleMap?.driver_name ?? 'Driver Name', // C
    titleMap?.conductor_name ?? 'Conductor Name', // D
    titleMap?.trip_out ?? 'Out', // E
    titleMap?.checked_in ?? 'Checked In', // F
    '', // G (signature)
    '', // H (remark)
  ]

  // Merges for table header
  ws.mergeCells(TABLE_HEADER_ROW1, 1, TABLE_HEADER_ROW2, 1) // SL NO vertical A
  ws.mergeCells(TABLE_HEADER_ROW1, 2, TABLE_HEADER_ROW2, 2) // Bus No vertical B
  ws.mergeCells(TABLE_HEADER_ROW1, 3, TABLE_HEADER_ROW1, 4) // Name of C:D horizontal
  ws.mergeCells(TABLE_HEADER_ROW1, 5, TABLE_HEADER_ROW1, 6) // Time E:F horizontal
  ws.mergeCells(TABLE_HEADER_ROW1, 7, TABLE_HEADER_ROW2, 7) // Signature vertical G
  ws.mergeCells(TABLE_HEADER_ROW1, 8, TABLE_HEADER_ROW2, 8) // Remark vertical H

  // Style header rows — top border removed from the first header row
  const headerRows: ExcelJS.Row[] = [ws.getRow(TABLE_HEADER_ROW1), ws.getRow(TABLE_HEADER_ROW2)]
headerRows.forEach((row: ExcelJS.Row) => {
  row.eachCell((cell: Cell) => {
    // Add all sides as thin borders
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
    cell.font = { bold: true }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })
  row.height = 18
})

  // -------------------------
  // 5) Column widths A..H so header content and remark show fully
  // -------------------------
/*const colWidths = [50, 50, 50,50,50, 50, 50, 50]; // all 8 columns
for (let i = 1; i <= colWidths.length; i++) {
  ws.getColumn(i).width = colWidths[i - 1];
}*/

// Apply alignment to all rows (header + data)
ws.eachRow((row) => {
  row.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })
})


  // -------------------------
  // 6) Fill data rows with explicit getCell assignments
  // -------------------------
  const positiveWords = ['checked in', 'checked', 'yes', 'present', 'signed', 'in', 'on time']
  const negativeWords = ['not checked in', 'not checked', 'no', 'absent', 'unsigned', 'not', 'late']
  const normalize = (v: any) => (v == null ? '' : String(v).toLowerCase())

  data.forEach((item: Record<string, any>, idx: number) => {
    const rowNum = DATA_START_ROW + idx
    const row = ws.getRow(rowNum)

    const signValue = item.sign ?? ''
    const remarkValue = item.status ?? ''

    row.getCell(1).value = idx + 1
    row.getCell(2).value = item.trip_bus ?? ''
    row.getCell(3).value = item.driver_name ?? ''
    row.getCell(4).value = item.conductor_name ?? ''
    row.getCell(5).value = item.trip_out ?? ''
    row.getCell(6).value = item.checked_in ?? ''
    row.getCell(7).value = signValue
    row.getCell(8).value = remarkValue

    row.getCell(1).alignment = { horizontal: 'center' }
    row.getCell(2).alignment = { horizontal: 'center' }
    row.getCell(5).alignment = { horizontal: 'center' }
    row.getCell(6).alignment = { horizontal: 'center' }
    row.getCell(7).alignment = { horizontal: 'center' }
    row.getCell(8).alignment = { horizontal: 'left', wrapText: true }

    const isPositive = (text: any) => {
      const s = normalize(text)
      if (!s) return false
      return positiveWords.some((p) => s === p || s.includes(p))
    }
    const isNegative = (text: any) => {
      const s = normalize(text)
      if (!s) return false
      return negativeWords.some((p) => s === p || s.includes(p))
    }

    const signCell = row.getCell(7)
    if (isPositive(signValue)) signCell.font = { color: { argb: 'FF008000' } }
    else if (isNegative(signValue)) signCell.font = { color: { argb: 'FFFF0000' } }

    const remarkCell = row.getCell(8)
    if (isPositive(remarkValue)) remarkCell.font = { color: { argb: 'FF008000' } }
    else if (isNegative(remarkValue)) remarkCell.font = { color: { argb: 'FFFF0000' } }
  })

  return ws
}
